import { Injectable, inject } from "@angular/core";
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from "@angular/router";
import { Observable, of } from "rxjs";
import { Currency } from "../models/currency";
import { CurrencyStateService } from "./currency-state.service";
import { CSP } from "../models/csp-api";

/**
 * CurrencyResolver initializes currency state BEFORE a route activates.
 *
 * This eliminates the need for components to handle currency initialization
 * and removes dependency on looker-studio-navigation component.
 *
 * Route Pattern: vcloudsmart/:version/:csp/:section/...
 * - Extracts :csp param (aws, gcp, azure, oci, drcc)
 * - Initializes currency from localStorage or defaults
 * - Ensures currency is ready before component ngOnInit
 *
 * Usage in routing:
 * {
 *   path: 'vcloudsmart/:version/:csp/:section/...',
 *   component: MyComponent,
 *   resolve: { currency: CurrencyResolver }
 * }
 *
 * Access in component:
 * constructor(private route: ActivatedRoute) {}
 * ngOnInit() {
 *   const currency = this.route.snapshot.data['currency'];
 * }
 */
@Injectable({
  providedIn: "root",
})
export class CurrencyResolver implements Resolve<Currency> {
  private currencyState = inject(CurrencyStateService);

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<Currency> {
    // Extract CSP from route params: :csp is always the 3rd segment in vcloudsmart routes
    const csp = this.extractCspFromRoute(route);

    if (!csp) {
      console.warn(`[CurrencyResolver] Could not extract CSP from route: ${state.url}`);
      this.currencyState.setActiveCsp("aws");
      // Return default AWS currency if CSP cannot be determined
      return of(this.currencyState.getCurrency("aws"));
    }

    this.currencyState.setActiveCsp(csp);

    // Initialize currency for this CSP (reads from localStorage if available)
    // This triggers: localStorage read → BehaviorSubject.next() → all subscribers notified
    const currency = this.currencyState.getCurrency(csp);

    console.log(`[CurrencyResolver] Resolved currency for ${csp}: ${currency.id}`);

    // Return immediately resolved (already cached in service)
    return of(currency);
  }

  /**
   * Extract CSP from route params
   * Route pattern: vcloudsmart/:version/:csp/:section/...
   * So :csp is always available in params by name 'csp'
   */
  private extractCspFromRoute(route: ActivatedRouteSnapshot): CSP | null {
    // Try to get from route params first (if route explicitly defines it)
    const cspParam = route.params["csp"] as CSP | undefined;
    if (cspParam && this.isValidCsp(cspParam)) {
      return cspParam;
    }

    // Fallback: parse from URL path (vcloudsmart/v2/aws/... → 'aws')
    const urlSegments = route.pathFromRoot.flatMap((r) => r.url.map((u) => u.path)).join("/");

    const match = urlSegments.match(/vcloudsmart\/[^/]+\/([^/]+)/);
    const csp = match?.[1] as CSP | undefined;

    return csp && this.isValidCsp(csp) ? csp : null;
  }

  /**
   * Validate CSP is one of the known cloud providers
   */
  private isValidCsp(value: string): value is CSP {
    return ["aws", "gcp", "azure", "oci", "drcc"].includes(value);
  }
}
